import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ymdLocal } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useSport } from "@/context/SportContext";
import SportSwitcher from "@/components/SportSwitcher";
import BookingActions from "@/components/BookingActions";
import { ChevronLeft, ChevronRight, Plus, Trash2, X, AlertTriangle, Users as UsersIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  group: { label: "Group", color: "#007AFF" },
  performance: { label: "Performance", color: "#FF4500" },
  one_on_one: { label: "1:1", color: "#CCFF00" },
};
const META = (k) => TYPE_META[k] || { label: (k || "Session").toString().replace(/_/g, " "), color: "#A3A3A3" };

// Iter93 — deep-link helper. Every "OPEN" button on this page navigates to
// `/offerings#offering-{id}` (which Offerings.jsx uses to scroll + volt-yellow
// highlight the card). Sessions whose `start` is in the past ARE still openable
// (coaches often need to review past sessions), but we surface a confirm so
// students don't accidentally click into an expired offering thinking they can
// still book it. Returns `true` when the caller should proceed with navigation.
function confirmIfExpired(startIso) {
  try {
    const start = new Date(startIso);
    if (!isNaN(start.getTime()) && start.getTime() < Date.now()) {
      // eslint config permits alerts — use native confirm for reliability
      return window.confirm("This has expired - do you still want to open it?");
    }
  } catch (_e) { /* fall through — never block on parse errors */ }
  return true;
}

// Coaches tapping their own past session open the read-only detail view.
function usePastSessionPath() {
  const { user } = useAuth();
  const admin = user?.role === "admin" || (user?.permissions || []).some((p) => p === "admin" || p === "super_admin");
  return (o, id = o?.id) => (id && ["coach", "admin"].includes(user?.role) && o?.start && new Date(o.start) < new Date()
    && (admin || o.coach_id === user?.id) ? `/offerings/${id}` : null);
}

export default function CalendarPage() {
  const { user } = useAuth();
  const { sport: globalSport } = useSport();
  const isCoach = user?.role === "coach" || user?.role === "admin";
  const isAdmin = (user?.permissions || []).some((p) => p === "admin" || p === "super_admin") || user?.role === "admin";
  const nav = useNavigate();
  const pastPath = usePastSessionPath();
  const [bookings, setBookings] = useState([]);
  const [students, setStudents] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [offerings, setOfferings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [cursor, setCursor] = useState(() => {
    const d = new Date(); d.setDate(1); d.setHours(0,0,0,0); return d;
  });
  const [modal, setModal] = useState(null); // {date}
  // Iter62: mini-preview state — {date, x, y}. Triggered by right-click (desktop)
  // or long-press ≥ LONG_PRESS_MS (mobile). Independent of the full DayModal so
  // coaches can triage without committing to a full-screen switch.
  const [preview, setPreview] = useState(null);
  const longPressTimer = useRef(null);
  const longPressFiredRef = useRef(false);
  const LONG_PRESS_MS = 500;
  // Admin view-as-coach selector — empty string means "all coaches" (default).
  const [viewCoachId, setViewCoachId] = useState("");
  // Iter87/91: agenda / month / rolling view. Server preference (users.calendar_view_pref)
  // wins if present; otherwise fall back to localStorage; otherwise 'rolling' for
  // coaches (their preferred default per iter91) and 'agenda' for students.
  const initialView = (
    (typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("view") : null)
    || user?.calendar_view_pref
    || (typeof window !== "undefined" ? localStorage.getItem("rl_cal_view") : null)
    || (isCoach ? "rolling" : "agenda")
  );
  const [view, setView] = useState(initialView);
  const urlViewRef = useRef(typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("view") : null);
  useEffect(() => {
    if (!view) return;
    if (urlViewRef.current && view === urlViewRef.current) return; // one-off link override, don't save as preference
    urlViewRef.current = null;
    try { localStorage.setItem("rl_cal_view", view); } catch (_e) { /* ignore */ }
    // Fire-and-forget persistence to profile; ignore failures (guest, expired token, etc).
    api.post("/me/calendar-view", { view }).catch(() => {});
  }, [view]);

  // Iter91 smart filters. Defaults per spec:
  //   students: "mine only" OFF (see everything bookable)
  //   coaches: "my sessions" + "team sessions" both ON
  //   everyone: past sessions hidden until "Show past sessions" is ticked
  const [showOnlyMine, setShowOnlyMine] = useState(false); // students
  const [showPast, setShowPast] = useState(false);
  const [coachShowMine, setCoachShowMine] = useState(true); // coaches
  const [coachShowTeam, setCoachShowTeam] = useState(true); // coaches

  // Index of offering IDs the player is enrolled in — used to visually highlight
  // their own bookings vs other coaches' open offerings on the month grid.
  const enrolledOfferingIds = useMemo(
    () => new Set((bookings || []).map((b) => b.offering_id).filter(Boolean)),
    [bookings]
  );

  const load = async () => {
    // When an admin is viewing a specific coach's calendar, scope both endpoints with
    // the coach_id query param. Plain coaches don't need this — their /bookings and
    // /offerings handlers already restrict to own + shared-team data server-side.
    const scopeQs = isAdmin && viewCoachId ? `&coach_id=${encodeURIComponent(viewCoachId)}` : "";
    const { data } = await api.get(`/bookings?_=1${scopeQs}`);
    setBookings(data);
    const [off, av] = await Promise.all([
      api.get(`/offerings?only_upcoming=false${scopeQs}`),
      isCoach ? api.get("/availability") : Promise.resolve({ data: [] }),
    ]);
    setOfferings(off.data);
    setBlocks(isCoach ? av.data : []);
    if (isCoach) {
      const s = await api.get("/students");
      setStudents(s.data);
      // Admins / super admins can view + book on behalf of any coach — fetch the roster.
      if (isAdmin) {
        try {
          const c = await api.get("/admin/users");
          setCoaches((c.data || []).filter((u) => ["coach", "admin"].includes(u.role)));
        } catch (_e) { /* ignore */ }
      }
    }
  };

  useEffect(() => { load();   }, [viewCoachId]);

  // Iter62: keyboard + outside-click dismissal for the mini-preview.
  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => { if (e.key === "Escape") setPreview(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [preview]);

  // Long-press helpers — arm on touchstart, cancel on move/end/leave.
  const startLongPress = (e, date) => {
    if (!date) return;
    longPressFiredRef.current = false;
    // Cache clientX/clientY at press-time since React pools synthetic events.
    const touch = e.touches?.[0];
    const x = touch?.clientX ?? 0;
    const y = touch?.clientY ?? 0;
    longPressTimer.current = setTimeout(() => {
      longPressFiredRef.current = true;
      setPreview({ date, x, y });
    }, LONG_PRESS_MS);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };
  const handleCellClick = (date) => {
    // Swallow the click that immediately follows a fired long-press so
    // touch devices don't open the full modal right after the preview.
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    if (date) setModal({ date });
  };

  const month = cursor.getMonth();
  const year = cursor.getFullYear();

  // Use ymdLocal() (from lib/utils) instead of `.toISOString().slice(0,10)`
  // for grid cells — the calendar cells are constructed at local midnight
  // and toISOString converts them to UTC, which was shifting availability
  // blocks / bookings by one day for BST users (see bug fix).
  const ymd = ymdLocal;

  const grid = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const startDow = (firstDay.getDay() + 6) % 7; // Monday-first
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [month, year]);

  const monthLabel = cursor.toLocaleString(undefined, { month: "long", year: "numeric" });

  // Iter91 — smart-filter helpers. Applied at render time (server already
  // narrows to accessible bookings; these filters are UX preference, not
  // security). `bookings` for students have student_id === user.id already
  // so the "mine only" toggle is a no-op for students most of the time, but
  // it also hides offering-preview rows the server may include.
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const notPast = (x) => showPast || new Date(x.start) >= todayStart;
  const filterBookings = (all) => {
    if (!Array.isArray(all)) return [];
    const list = all.filter(notPast);
    if (isCoach) {
      return list.filter((b) => {
        const own = b.coach_id === user?.id;
        if (own) return coachShowMine;
        return coachShowTeam;
      });
    }
    if (showOnlyMine) return list.filter((b) => b.student_id === user?.id);
    return list;
  };
  const filterOfferings = (all) => {
    if (!Array.isArray(all)) return [];
    const list = all.filter(notPast);
    if (isCoach) {
      return list.filter((o) => {
        const own = o.coach_id === user?.id;
        if (own) return coachShowMine;
        return coachShowTeam;
      });
    }
    // Iter93e — students: when "Show only my bookings" is ON, we filter
    // offerings down to just those the student has already joined. The
    // `enrolledOfferingIds` Set is derived (line 85) from bookings' offering_id
    // fields, so it's exactly the offerings this student is enrolled in.
    if (showOnlyMine) return list.filter((o) => enrolledOfferingIds.has(o.id));
    return list;
  };

  // Iter93d — Filtered projections. Compute the smart-filter-respecting
  // `shownBookings` / `shownOfferings` once (memoised) so Month grid, Day
  // Modal AND Day Preview all read from the SAME filtered surface. Before
  // iter93d only the Rolling / Agenda views were passed pre-filtered lists;
  // the Month grid built its cells from raw `bookings`/`offerings`, which
  // meant toggling "My Sessions" or "Team Sessions" had no effect on the
  // month grid or the modals opened from it.
  const shownBookings = useMemo(() => filterBookings(bookings),
    [bookings, isCoach, coachShowMine, coachShowTeam, showOnlyMine, showPast, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const shownOfferings = useMemo(() => filterOfferings(offerings),
    [offerings, isCoach, coachShowMine, coachShowTeam, showOnlyMine, showPast, enrolledOfferingIds, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const bookingsOn = (date) => {
    if (!date) return [];
    const ds = ymd(date);
    return shownBookings.filter((b) => b.start.slice(0, 10) === ds);
  };

  // Split active vs cancelled — cancelled render faintly on the month grid
  // + get their own group in the day modal so coaches can see churn at a
  // glance without them cluttering the main list.
  const isCancelled = (b) => b.status === "cancelled";

  const offeringsOn = (date) => {
    if (!date) return [];
    const ds = ymd(date);
    return shownOfferings.filter((o) => o.start.slice(0, 10) === ds);
  };

  const isBlocked = (date) => {
    if (!date) return false;
    const ds = ymd(date);
    return blocks.some((b) => ds >= b.start.slice(0,10) && ds <= b.end.slice(0,10));
  };

  const viewHeading = ({
    agenda: "Rolling schedule",
    rolling: "Rolling view",
    month: monthLabel,
  })[view] || monthLabel;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between fade-up flex-wrap gap-3">
        <div>
          <div className="label-mono mb-2">CALENDAR</div>
          <h1 className="h-display text-5xl tracking-tighter uppercase" data-testid="cal-month-label">{viewHeading}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Admin-only coach-switcher — lets the site owner peek at any coach's diary.
              Plain coaches don't see this; their /bookings + /offerings already scope to
              their own + team-shared data on the server. */}
          {isAdmin && coaches.length > 0 && (
            <select
              value={viewCoachId}
              onChange={(e) => setViewCoachId(e.target.value)}
              data-testid="admin-coach-switcher"
              className="bg-[#141414] border border-[#262626] hover:border-white px-3 py-2 text-xs uppercase tracking-wider focus:outline-none focus:border-[#007AFF]"
              title="View another coach's calendar"
            >
              <option value="">All coaches (default)</option>
              {coaches.map((c) => (
                <option key={c.id} value={c.id}>{c.name || c.email}</option>
              ))}
            </select>
          )}
          <SportSwitcher className="mr-2" />
          {/* Iter87: agenda ↔ month toggle. Agenda is the default; month
              stays available for coaches who want the wall view. */}
          <div className="inline-flex border border-[#262626]" data-testid="cal-view-toggle">
            <button
              type="button"
              onClick={() => setView("agenda")}
              className={`min-h-[40px] px-3 py-2 text-[10px] uppercase tracking-wider transition-colors ${view === "agenda" ? "bg-[#CCFF00] text-black font-bold" : "text-[#A3A3A3] hover:text-white"}`}
              data-testid="cal-view-agenda"
            >
              Schedule
            </button>
            <button
              type="button"
              onClick={() => setView("rolling")}
              className={`min-h-[40px] px-3 py-2 text-[10px] uppercase tracking-wider transition-colors border-l border-[#262626] ${view === "rolling" ? "bg-[#CCFF00] text-black font-bold" : "text-[#A3A3A3] hover:text-white"}`}
              data-testid="cal-view-rolling"
            >
              Rolling
            </button>
            <button
              type="button"
              onClick={() => setView("month")}
              className={`min-h-[40px] px-3 py-2 text-[10px] uppercase tracking-wider transition-colors border-l border-[#262626] ${view === "month" ? "bg-[#CCFF00] text-black font-bold" : "text-[#A3A3A3] hover:text-white"}`}
              data-testid="cal-view-month"
            >
              Month
            </button>
          </div>
          {view === "month" && (
            <>
              <button data-testid="cal-prev" onClick={() => setCursor(new Date(year, month - 1, 1))} className="p-2 border border-[#262626] hover:border-white"><ChevronLeft className="w-4 h-4" /></button>
              <button data-testid="cal-today" onClick={() => { const d = new Date(); d.setDate(1); setCursor(d); }} className="px-4 py-2 border border-[#262626] hover:border-white text-xs uppercase tracking-wider">Today</button>
              <button data-testid="cal-next" onClick={() => setCursor(new Date(year, month + 1, 1))} className="p-2 border border-[#262626] hover:border-white"><ChevronRight className="w-4 h-4" /></button>
            </>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 fade-up d1">
        {Object.entries(TYPE_META).map(([k, m]) => (
          <div key={k} className="flex items-center gap-2 text-xs">
            <span className="w-3 h-3" style={{ background: m.color }} />
            <span className="label-mono">{m.label}</span>
          </div>
        ))}
        {!isCoach && (
          <div className="flex items-center gap-2 text-xs" data-testid="legend-enrolled">
            <span className="w-3 h-3" style={{ background: "#CCFF00", boxShadow: "0 0 0 1px #CCFF00 inset, 0 0 6px #CCFF00" }} />
            <span className="label-mono text-[#CCFF00]">Your booking</span>
          </div>
        )}
        {isCoach && (
          <div className="flex items-center gap-2 text-xs">
            <span className="w-3 h-3" style={{ backgroundImage: "repeating-linear-gradient(45deg, rgba(255,69,0,0.6) 0px, rgba(255,69,0,0.6) 2px, transparent 2px, transparent 4px)" }} />
            <span className="label-mono text-[#FF4500]">Unavailable</span>
          </div>
        )}
        <div className="ml-auto label-mono">{isCoach ? "Pricing set per coach" : "Tap an offering to enrol"}</div>
      </div>

      {/* Iter91 — Smart filters. Students: mine-only toggle. Coaches: mine +
          team checkboxes. Both default to ON so the coach's own view is
          uncluttered. Applied in-place to the render-time booking list. */}
      <div className="flex flex-wrap items-center gap-3 fade-up d1" data-testid="cal-smart-filters">
        <label className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#A3A3A3] cursor-pointer">
          <input
            type="checkbox"
            className="w-4 h-4 accent-[#CCFF00]"
            data-testid="filter-show-past"
            checked={showPast}
            onChange={(e) => setShowPast(e.target.checked)}
          />
          Show past sessions
        </label>
        {!isCoach && !isAdmin && (
          <label className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#A3A3A3] cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 accent-[#CCFF00]"
              data-testid="filter-only-mine"
              checked={showOnlyMine}
              onChange={(e) => setShowOnlyMine(e.target.checked)}
            />
            Show only my bookings
          </label>
        )}
        {isCoach && (
          <>
            <label className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#A3A3A3] cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 accent-[#CCFF00]"
                data-testid="filter-coach-mine"
                checked={coachShowMine}
                onChange={(e) => setCoachShowMine(e.target.checked)}
              />
              My sessions
            </label>
            <label className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#A3A3A3] cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 accent-[#5B8DEF]"
                data-testid="filter-coach-team"
                checked={coachShowTeam}
                onChange={(e) => setCoachShowTeam(e.target.checked)}
              />
              Team sessions
            </label>
          </>
        )}
      </div>

      {/* Iter91 — Rolling View. Previous week + current (TODAY highlighted) +
          next 3 weeks. Coach's own sessions render volt-yellow; team-shared
          sessions render blue for at-a-glance distinction. */}
      {view === "rolling" && (
        <RollingView
          bookings={shownBookings}
          offerings={shownOfferings}
          currentUserId={user?.id}
          isCoach={isCoach}
          isCancelled={isCancelled}
          onDayClick={(date) => setModal({ date })}
          onDayContext={(date, e) => { e.preventDefault(); setPreview({ date, x: e.clientX, y: e.clientY }); }}
          nav={nav}
        />
      )}

      {/* Iter87 — Rolling Schedule (agenda) view. */}
      {view === "agenda" && (
        <AgendaView
          bookings={shownBookings}
          offerings={shownOfferings}
          enrolledOfferingIds={enrolledOfferingIds}
          isCoach={isCoach}
          isCancelled={isCancelled}
          showPast={showPast}
          onDayClick={(date) => setModal({ date })}
        />
      )}

      {/* Calendar grid — kept as an optional toggle. */}
      {view === "month" && (
      <div className="card-surface p-2 fade-up d2">
        <div className="grid grid-cols-7 border-b border-[#262626]">
          {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => (
            <div key={d} className="label-mono p-3 text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((date, i) => {
            const items = bookingsOn(date);
            const offs = offeringsOn(date);
            const blocked = isBlocked(date);
            const isToday = date && new Date().toDateString() === date.toDateString();
            return (
              <div
                key={i}
                onClick={() => handleCellClick(date)}
                onContextMenu={(e) => {
                  if (!date) return;
                  e.preventDefault();
                  setPreview({ date, x: e.clientX, y: e.clientY });
                }}
                onTouchStart={(e) => startLongPress(e, date)}
                onTouchEnd={cancelLongPress}
                onTouchMove={cancelLongPress}
                onTouchCancel={cancelLongPress}
                data-testid={date ? `day-${date.getDate()}` : undefined}
                data-blocked={date ? (blocked ? "true" : "false") : undefined}
                className={`min-h-[112px] border-r border-b border-[#262626] p-2 cursor-pointer transition-colors hover:bg-[#141414] relative select-none ${!date ? "bg-[#0a0a0a] opacity-30 cursor-default" : ""} ${isToday ? "bg-[#141414]" : ""}`}
                style={blocked ? { backgroundImage: "repeating-linear-gradient(45deg, rgba(255,69,0,0.08) 0px, rgba(255,69,0,0.08) 6px, transparent 6px, transparent 12px)" } : {}}
              >
                {date && (
                  <>
                    <div className="flex items-start justify-between mb-1">
                      <div className={`text-sm font-semibold ${isToday ? "text-[#CCFF00]" : ""}`}>{date.getDate()}</div>
                      {blocked && <span className="label-mono text-[0.55rem] text-[#FF4500]">OFF</span>}
                    </div>
                    <div className="space-y-1">
                      {offs.slice(0, 2).map((o) => {
                        const mine = enrolledOfferingIds.has(o.id);
                        return (
                          <div key={o.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isCoach && mine) { nav(`/session/${o.id}`); return; }
                              if (pastPath(o)) { nav(pastPath(o)); return; }
                              if (confirmIfExpired(o.start)) nav(`/offerings#offering-${o.id}`);
                            }}
                            data-testid={mine ? `offering-mine-${o.id}` : `offering-${o.id}`}
                            className="text-[10px] px-1.5 py-0.5 truncate font-semibold hover:brightness-110 cursor-pointer"
                            style={mine
                              ? { background: "#CCFF00", color: "#0A0A0A", boxShadow: "0 0 0 1px #CCFF00 inset, 0 0 8px rgba(204,255,0,0.5)" }
                              : { background: META(o.session_type).color + "33", borderLeft: `2px solid ${META(o.session_type).color}` }}>
                            {mine && "★ "}{new Date(o.start).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} · {o.title?.slice(0, 22)}
                          </div>
                        );
                      })}
                      {items.slice(0, 3 - Math.min(offs.length, 2)).map((b) => {
                        const cancelled = isCancelled(b);
                        return (
                          <div
                            key={b.id}
                            onClick={!isCoach ? (e) => { e.stopPropagation(); nav(`/session/${b.id}`); }
                              : (!cancelled && pastPath(b, b.offering_id)) ? (e) => { e.stopPropagation(); nav(pastPath(b, b.offering_id)); } : undefined}
                            data-testid={cancelled ? `booking-cancelled-${b.id}` : `booking-${b.id}`}
                            className="text-[10px] px-1.5 py-0.5 truncate"
                            style={cancelled
                              ? {
                                  background: "transparent",
                                  borderLeft: `2px solid ${META(b.session_type).color}55`,
                                  color: "#525252",
                                  textDecoration: "line-through",
                                  opacity: 0.7,
                                }
                              : { background: META(b.session_type).color + "22", borderLeft: `2px solid ${META(b.session_type).color}` }}
                          >
                            {new Date(b.start).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} {isCoach ? b.student_name : META(b.session_type).label}
                          </div>
                        );
                      })}
                      {items.length + offs.length > 3 && <div className="text-[10px] text-[#A3A3A3]">+{items.length + offs.length - 3}</div>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
      )}

      {modal && (
        <DayModal
          date={modal.date}
          bookings={bookingsOn(modal.date)}
          offerings={offeringsOn(modal.date)}
          enrolledOfferingIds={enrolledOfferingIds}
          isCoach={isCoach}
          students={students}
          coaches={coaches}
          me={user}
          defaultSport={globalSport}
          onClose={() => setModal(null)}
          onChange={load}
        />
      )}

      {preview && (
        <DayPreview
          date={preview.date}
          x={preview.x}
          y={preview.y}
          bookings={bookingsOn(preview.date)}
          offerings={offeringsOn(preview.date)}
          isCoach={isCoach}
          onOpenFull={() => { setPreview(null); setModal({ date: preview.date }); }}
          onClose={() => setPreview(null)}
          onChange={load}
        />
      )}
    </div>
  );
}

function DayModal({ date, bookings, offerings = [], enrolledOfferingIds = new Set(), isCoach, students, coaches = [], me, defaultSport, onClose, onChange }) {
  const isSuperAdmin = (me?.permissions || []).includes("super_admin");
  const [type, setType] = useState("one_on_one");
  const [sport, setSport] = useState(defaultSport || "squash");
  const [time, setTime] = useState("10:00");
  const [duration, setDuration] = useState(60);
  const [notes, setNotes] = useState("");
  const [studentIds, setStudentIds] = useState([]); // multi-select set
  const [targetCoachId, setTargetCoachId] = useState(""); // super-admin only
  const [loading, setLoading] = useState(false);
  const [conflicts, setConflicts] = useState(null); // {hard, soft, by_owner}
  const [forceBook, setForceBook] = useState(false);

  const isMulti = type === "group" || type === "performance";

  // Reset selections when type flips between single/multi so we don't accidentally send 2 students for a 1:1
  useEffect(() => {
    if (!isMulti && studentIds.length > 1) setStudentIds(studentIds.slice(0, 1));
    setForceBook(false);
    setConflicts(null);
  }, [type]);  

  const toggleStudent = (id) => {
    setStudentIds((prev) => {
      if (prev.includes(id)) return prev.filter((s) => s !== id);
      if (!isMulti) return [id]; // 1:1 — single only
      return [...prev, id];
    });
  };

  const buildPayload = () => {
    const [h, m] = time.split(":").map(Number);
    const dt = new Date(date);
    dt.setHours(h, m, 0, 0);
    const payload = { session_type: type, sport, start: dt.toISOString(), duration_min: Number(duration), notes };
    if (isCoach) {
      payload.student_ids = studentIds;
      if (isSuperAdmin && targetCoachId) payload.target_coach_id = targetCoachId;
      if (isSuperAdmin && forceBook) payload.force = true;
    }
    return payload;
  };

  const checkConflicts = async (payload) => {
    if (!isCoach) return null;
    try {
      const coachToCheck = (isSuperAdmin && targetCoachId) ? targetCoachId : me?.id;
      const { data } = await api.post("/bookings/check-conflict", {
        start: payload.start,
        duration_min: payload.duration_min,
        coach_id: coachToCheck,
        student_ids: studentIds,
      });
      const hasHard = (data.hard || []).length > 0;
      const hasSoft = (data.soft || []).length > 0;
      return (hasHard || hasSoft) ? data : null;
    } catch (_e) { return null; }
  };

  const submitBooking = async (payload) => {
    setLoading(true);
    try {
      await api.post("/bookings", payload);
      toast.success(payload.student_ids?.length > 1 ? `${payload.student_ids.length} sessions booked` : "Session booked");
      onChange();
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail && detail.type === "booking_conflict") {
        // Show inline banner; super-admin can flip the force toggle.
        setConflicts({ hard: detail.hard || [], soft: [], by_owner: {}, force_available: !!detail.force_available });
        toast.error("Conflict — overlap with existing booking(s). See banner above.");
      } else {
        toast.error(formatApiError(detail) || "Failed");
      }
    } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (isCoach && !studentIds.length) {
      toast.error("Pick at least one student");
      return;
    }
    if (isCoach && !isMulti && studentIds.length !== 1) {
      toast.error("1:1 sessions accept exactly one student");
      return;
    }
    const payload = buildPayload();
    // Pre-flight conflict scan — render banner before the user clicks
    const conf = await checkConflicts(payload);
    setConflicts(conf);
    if (conf && (conf.hard || []).length > 0 && !(isSuperAdmin && forceBook)) {
      toast.error("Hard conflict — resolve or super-admin force.");
      return;
    }
    return submitBooking(payload);
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/bookings/${id}`); toast.success("Removed"); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="card-surface w-full max-w-xl max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
        <div className="flex items-center justify-between p-6 border-b border-[#262626]">
          <div>
            <div className="label-mono mb-1">SESSIONS</div>
            <h2 className="h-display text-2xl">{date.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</h2>
          </div>
          <button onClick={onClose} data-testid="modal-close"><X className="w-5 h-5" /></button>
        </div>

        {/* Existing */}
        <div className="p-6 border-b border-[#262626]">
          <div className="label-mono mb-3">{isCoach ? "Existing" : "Your bookings"}</div>
          {(() => {
            const active = bookings.filter((b) => b.status !== "cancelled");
            const cancelled = bookings.filter((b) => b.status === "cancelled");
            if (!bookings.length) return <div className="text-xs text-[#A3A3A3]">Nothing booked.</div>;
            return (
              <>
                {active.map((b) => (
                  <div key={b.id} className="flex items-center gap-3 py-2 border-b border-[#262626] last:border-0 flex-wrap">
                    <span className="w-2 h-2" style={{ background: META(b.session_type).color }} />
                    <div className="flex-1 min-w-[180px]">
                      <div className="text-sm font-semibold">{new Date(b.start).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} · {isCoach ? b.student_name : "Your session"}</div>
                      <div className="text-xs text-[#A3A3A3]">{META(b.session_type).label} · {b.duration_min} min · £{b.price}</div>
                    </div>
                    <BookingActions bookingId={b.id} onChange={onChange} compact />
                    {isCoach && !(b.start && new Date(b.start) < new Date()) && (
                      <button onClick={() => handleDelete(b.id)} className="text-[#FF4500] hover:opacity-70" data-testid={`del-${b.id}`} title="Hard delete (admin)"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
                {!active.length && (
                  <div className="text-xs text-[#A3A3A3]">No active bookings.</div>
                )}
                {cancelled.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-dashed border-[#262626]" data-testid="cancelled-bookings-group">
                    <div className="label-mono text-[10px] text-[#525252] mb-2">CANCELLED · {cancelled.length}</div>
                    {cancelled.map((b) => (
                      <div key={b.id} data-testid={`cancelled-booking-${b.id}`} className="flex items-center gap-3 py-1.5 opacity-60">
                        <span className="w-2 h-2" style={{ background: META(b.session_type).color, opacity: 0.5 }} />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-[#A3A3A3] line-through truncate">
                            {new Date(b.start).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} · {isCoach ? b.student_name : "Your session"}
                          </div>
                          <div className="text-[10px] text-[#525252] uppercase tracking-wider">
                            {META(b.session_type).label} · Cancelled{b.cancelled_at ? ` ${new Date(b.cancelled_at).toLocaleDateString(undefined, { day: "2-digit", month: "short" })}` : ""}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* STUDENT VIEW — list of available offerings on this day, with one-tap Enrol */}
        {!isCoach && (
          <div className="p-6 space-y-3" data-testid="student-day-offerings">
            <div className="flex items-center justify-between">
              <div className="label-mono">Available sessions</div>
              <a href="/offerings" className="text-[10px] uppercase tracking-wider text-[#CCFF00] hover:underline">All offerings →</a>
            </div>
            {offerings.length === 0 && (
              <div className="text-xs text-[#A3A3A3]">No coach offerings on this day. Browse all offerings to find one that fits.</div>
            )}
            {offerings.map((o) => {
              const mine = enrolledOfferingIds.has(o.id);
              const spacesLeft = Math.max(0, (o.max_spaces || 1) - (o.enrolled_student_ids?.length || 0));
              return (
                <a key={o.id}
                  href={`/offerings#offering-${o.id}`}
                  data-testid={`enrol-link-${o.id}`}
                  className="flex items-center gap-3 p-3 border transition-colors hover:bg-[#141414]"
                  style={mine
                    ? { borderColor: "#CCFF00", background: "rgba(204,255,0,0.06)" }
                    : { borderColor: "#262626" }}>
                  <span className="w-1.5 self-stretch min-h-[40px]" style={{ background: META(o.session_type).color }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold flex items-center gap-2">
                      {mine && <span className="text-[#CCFF00] text-[10px] font-bold uppercase tracking-wider">★ Booked</span>}
                      <span className="truncate">{o.title || META(o.session_type).label}</span>
                    </div>
                    <div className="text-[10px] text-[#A3A3A3] mt-0.5 truncate">
                      {new Date(o.start).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} · {o.duration_min}min · {o.venue || "TBC"} · £{o.price_per_player ?? o.price ?? 0}
                    </div>
                  </div>
                  <div className="text-[10px] label-mono text-right">
                    {mine ? <span className="text-[#CCFF00]">YOURS</span> : <span className={spacesLeft <= 0 ? "text-[#FF4500]" : "text-[#A3A3A3]"}>{spacesLeft <= 0 ? "Full" : `${spacesLeft} left`}</span>}
                  </div>
                </a>
              );
            })}
          </div>
        )}

        {/* COACH/ADMIN — create new session controls */}
        {isCoach && (
        <div className="p-6 space-y-4">
          <div className="label-mono">Book new session</div>

          {/* Conflict banner — appears once we detect overlap */}
          {conflicts && ((conflicts.hard || []).length > 0 || (conflicts.soft || []).length > 0) && (
            <div
              data-testid="conflict-banner"
              className="border-l-4 px-4 py-3 flex items-start gap-3"
              style={{ borderColor: "#FF4500", background: "#1a0a05", color: "#FFD3C2" }}
            >
              <AlertTriangle className="w-5 h-5 text-[#FF4500] mt-0.5 shrink-0" />
              <div className="flex-1 text-sm">
                <div className="font-bold text-[#FF4500] mb-1">CONFLICT — overlapping booking{(conflicts.hard || []).length === 1 ? "" : "s"} detected</div>
                <ul className="space-y-0.5 text-xs">
                  {(conflicts.hard || []).map((c, i) => (
                    <li key={`h-${i}`}>• {c.reason || c.booking?.start}</li>
                  ))}
                  {(conflicts.soft || []).map((c, i) => (
                    <li key={`s-${i}`} className="text-[#FFE9A8]">• {c.reason}</li>
                  ))}
                </ul>
                {isSuperAdmin && (conflicts.force_available !== false) && (conflicts.hard || []).length > 0 ? (
                  <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="force-book-toggle">
                    <input type="checkbox" checked={forceBook} onChange={(e) => setForceBook(e.target.checked)}
                      className="w-4 h-4 accent-[#FF4500]" />
                    <span className="text-xs text-[#FFE9A8]">Super-admin override: force this booking anyway (audit-logged)</span>
                  </label>
                ) : (conflicts.hard || []).length > 0 ? (
                  <div className="mt-2 text-xs text-[#A3A3A3]">Change the time, duration or selected student(s) to clear this conflict. Only a super admin can override.</div>
                ) : null}
              </div>
            </div>
          )}

          <div>
            <div className="label-mono mb-2">Sport</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "squash", l: "Squash", c: "#CCFF00" },
                { v: "pickleball", l: "Pickleball", c: "#FF4500" },
              ].map((sp) => (
                <button key={sp.v} type="button" onClick={() => setSport(sp.v)} data-testid={`sport-pick-${sp.v}`}
                  className="py-3 text-xs uppercase tracking-wider border transition-colors"
                  style={sport === sp.v ? { background: sp.c, color: "#0A0A0A", borderColor: sp.c, fontWeight: 700 } : { borderColor: "#262626", color: "#A3A3A3" }}>
                  {sp.l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="label-mono mb-2">Type</div>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(TYPE_META).map(([k, m]) => (
                <button key={k} type="button" onClick={() => setType(k)} data-testid={`type-${k}`}
                  className={`py-3 text-xs uppercase tracking-wider border transition-colors ${type === k ? "bg-white text-black font-bold" : "border-[#262626] hover:border-white"}`}
                  style={type === k ? { background: m.color, color: "#0A0A0A", borderColor: m.color } : {}}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Super-admin only — book on a different coach's behalf */}
          {isSuperAdmin && coaches.length > 0 && (
            <div data-testid="coach-picker">
              <div className="label-mono mb-2">Coach (super-admin)</div>
              <select value={targetCoachId} onChange={(e) => setTargetCoachId(e.target.value)} data-testid="select-target-coach"
                className="w-full bg-[#141414] border border-[#CCFF00] px-3 py-2.5 text-sm focus:outline-none focus:border-white">
                <option value="">— Book as me ({me?.name}) —</option>
                {coaches.filter((c) => c.id !== me?.id).map((c) => (
                  <option key={c.id} value={c.id}>{c.name} · {c.email}</option>
                ))}
              </select>
              {targetCoachId && (
                <div className="text-[10px] text-[#CCFF00] mt-1 uppercase tracking-wider">
                  Booking on behalf of {coaches.find((c) => c.id === targetCoachId)?.name} · audit-logged
                </div>
              )}
            </div>
          )}

          {isCoach && (
            <div>
              <div className="label-mono mb-2 flex items-center justify-between">
                <span>{isMulti ? "Students (multi-select)" : "Student"}</span>
                {isMulti && <span className="text-[10px] normal-case text-[#A3A3A3]">{studentIds.length} selected</span>}
              </div>
              {isMulti ? (
                <div className="max-h-44 overflow-y-auto border border-[#262626] divide-y divide-[#262626]" data-testid="student-multiselect">
                  {students.length === 0 && <div className="px-3 py-2 text-xs text-[#A3A3A3]">No players yet.</div>}
                  {students.map((s) => {
                    const checked = studentIds.includes(s.id);
                    return (
                      <label key={s.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-[#141414]"
                        data-testid={`multi-student-${s.id}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleStudent(s.id)}
                          className="w-4 h-4 accent-[#CCFF00]" />
                        <span className="text-sm flex-1">{s.name}</span>
                        <span className="text-[10px] text-[#525252]">{s.email}</span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <select value={studentIds[0] || ""} onChange={(e) => setStudentIds(e.target.value ? [e.target.value] : [])} data-testid="select-student"
                  className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]">
                  <option value="">— Select —</option>
                  {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="label-mono mb-2">Time</div>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="booking-time"
                className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]" />
            </div>
            <div>
              <div className="label-mono mb-2">Duration (min)</div>
              <input type="number" value={duration} min={15} step={15} onChange={(e) => setDuration(e.target.value)} data-testid="booking-duration"
                className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]" />
            </div>
          </div>
          <div>
            <div className="label-mono mb-2">Notes</div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="booking-notes"
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]" />
          </div>

          <button onClick={handleCreate} disabled={loading} data-testid="booking-submit"
            className="btn-volt w-full py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
            <Plus className="w-4 h-4" /> {loading ? "Booking…" : isMulti && studentIds.length > 1 ? `Book ${studentIds.length} sessions` : "Book session"}
          </button>
        </div>
        )}
      </div>
    </div>
  );
}

// ---- Iter62: DayPreview -----------------------------------------------------
// Compact mini-card anchored near the cursor / long-press point. Shows the
// day's sessions at-a-glance with per-booking Cancel/Reschedule (via
// BookingActions) + a drill-in link to /players/{student_id}. Independent of
// the full DayModal so triage never requires a context switch.
function DayPreview({ date, x, y, bookings, offerings, isCoach, onOpenFull, onClose, onChange }) {
  const pastPath = usePastSessionPath();
  // Ignore the synthetic click that follows a mobile long-press touchend — it
  // lands on the overlay and would otherwise auto-dismiss the preview the user
  // just summoned. Desktop right-click unaffected (no touchend, no synth click).
  const mountedAtRef = useRef(Date.now());
  const CLICK_GUARD_MS = 400;
  const guardedClose = (e) => {
    if (Date.now() - mountedAtRef.current < CLICK_GUARD_MS) return;
    onClose(e);
  };
  // Clamp position so the card stays within the viewport (~320 wide, ~360 tall estimate)
  const PREV_W = 340;
  const PREV_H = 380;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1440;
  const vh = typeof window !== "undefined" ? window.innerHeight : 900;
  const left = Math.max(8, Math.min((x || 0) + 8, vw - PREV_W - 8));
  const top = Math.max(8, Math.min((y || 0) + 8, vh - PREV_H - 8));

  const active = bookings.filter((b) => b.status !== "cancelled");
  const cancelled = bookings.filter((b) => b.status === "cancelled");
  const dateLabel = date.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

  return (
    <div
      className="fixed inset-0 z-40"
      onClick={guardedClose}
      onTouchEnd={(e) => e.stopPropagation()}
      onContextMenu={(e) => { e.preventDefault(); onClose(); }}
      data-testid="day-preview-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.stopPropagation()}
        className="card-surface shadow-2xl border border-[#262626]"
        style={{ position: "absolute", left, top, width: PREV_W, maxHeight: PREV_H, overflow: "hidden", display: "flex", flexDirection: "column" }}
        data-testid="day-preview"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#262626] shrink-0">
          <div>
            <div className="label-mono text-[10px] text-[#A3A3A3]">QUICK PREVIEW</div>
            <div className="text-sm font-semibold">{dateLabel}</div>
          </div>
          <button onClick={onClose} data-testid="preview-close" className="text-[#A3A3A3] hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {active.length === 0 && offerings.length === 0 && cancelled.length === 0 && (
            <div className="text-xs text-[#A3A3A3] py-4">Nothing on this day yet.</div>
          )}

          {active.map((b) => (
            <div key={b.id} data-testid={`preview-booking-${b.id}`} className="border border-[#262626] p-2">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="w-1.5 h-8 shrink-0" style={{ background: META(b.session_type).color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold truncate">
                    {new Date(b.start).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} · {isCoach ? (b.student_name || "Player") : "Your session"}
                  </div>
                  <div className="text-[10px] text-[#A3A3A3]">{META(b.session_type).label} · {b.duration_min}m · £{b.price || 0}</div>
                </div>
                {isCoach && b.student_id && (
                  <Link
                    to={`/players/${b.student_id}`}
                    data-testid={`preview-open-player-${b.id}`}
                    onClick={onClose}
                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 border border-[#262626] hover:border-[#CCFF00] inline-flex items-center gap-1"
                    title="Open player profile"
                  >
                    <ExternalLink className="w-3 h-3" /> Open
                  </Link>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <BookingActions bookingId={b.id} onChange={onChange} compact />
              </div>
            </div>
          ))}

          {offerings.map((o) => (
            <Link
              key={o.id}
              to={pastPath(o) || `/offerings#offering-${o.id}`}
              onClick={(e) => {
                if (!pastPath(o) && !confirmIfExpired(o.start)) { e.preventDefault(); return; }
                onClose();
              }}
              data-testid={`preview-offering-${o.id}`}
              className="flex items-center gap-2 border border-[#262626] hover:border-[#CCFF00] p-2"
            >
              <span className="w-1.5 h-8 shrink-0" style={{ background: META(o.session_type).color }} />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold truncate">
                  {new Date(o.start).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} · {o.title || META(o.session_type).label}
                </div>
                <div className="text-[10px] text-[#A3A3A3]">{o.duration_min}m · {o.enrolled_student_ids?.length || 0}/{o.max_spaces || 1} · £{o.price_per_player ?? o.price ?? 0}</div>
              </div>
              <ExternalLink className="w-3 h-3 text-[#A3A3A3] shrink-0" />
            </Link>
          ))}

          {cancelled.length > 0 && (
            <div className="pt-2 border-t border-dashed border-[#262626]" data-testid="preview-cancelled">
              <div className="label-mono text-[9px] text-[#525252] mb-1">CANCELLED · {cancelled.length}</div>
              {cancelled.map((b) => (
                <div key={b.id} className="text-[10px] text-[#525252] line-through truncate py-0.5">
                  {new Date(b.start).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} · {b.student_name || "Player"}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-[#262626] px-4 py-2 shrink-0">
          <button
            onClick={onOpenFull}
            data-testid="preview-open-full"
            className="w-full py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-[#141414] transition-colors inline-flex items-center justify-center gap-1"
          >
            Open full day · book new session <Plus className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Iter87 — Rolling agenda list. Groups bookings + offerings from today
// forward into per-day sections, upcoming-first. Cancelled bookings render
// faintly so the coach still sees the historical intent without visual noise.
function AgendaView({ bookings, offerings, enrolledOfferingIds, isCoach, isCancelled, showPast, onDayClick }) {
  const agendaNav = useNavigate();
  const pastPath = usePastSessionPath();
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  // Merge bookings + offerings into one flat, chronologically sorted list.
  const merged = [];
  (bookings || []).forEach((b) => {
    const d = new Date(b.start);
    if (d < now && !showPast) return; // past hidden unless "Show past sessions"
    merged.push({ kind: "booking", when: d, item: b });
  });
  (offerings || []).forEach((o) => {
    const d = new Date(o.start);
    if (d < now && !showPast) return;
    merged.push({ kind: "offering", when: d, item: o });
  });
  merged.sort((a, b) => a.when - b.when);

  // Group by yyyy-mm-dd for section headers.
  const groups = new Map();
  for (const row of merged) {
    const key = row.when.toDateString();
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  }

  if (groups.size === 0) {
    return (
      <div className="card-surface p-8 text-center fade-up d2" data-testid="agenda-empty">
        <div className="label-mono text-[#A3A3A3] mb-2">NOTHING UPCOMING</div>
        <div className="text-sm text-[#525252]">No sessions on your schedule yet. Publish an offering or wait for a booking to show up here.</div>
      </div>
    );
  }

  const fmtDay = (d) => {
    const today = new Date(); today.setHours(0,0,0,0);
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
    const day = new Date(d); day.setHours(0,0,0,0);
    if (day.getTime() === today.getTime()) return "Today";
    if (day.getTime() === tomorrow.getTime()) return "Tomorrow";
    return day.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" });
  };
  const fmtTime = (d) => d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="space-y-6 fade-up d2" data-testid="agenda-view">
      {Array.from(groups.entries()).map(([key, rows]) => {
        const date = new Date(key);
        return (
          <div key={key} data-testid={`agenda-day-${date.toISOString().slice(0,10)}`}>
            <button
              type="button"
              onClick={() => onDayClick(date)}
              className="w-full text-left mb-2 label-mono text-[#A3A3A3] hover:text-white active:scale-[0.99] transition-all"
              data-testid={`agenda-day-header-${date.toISOString().slice(0,10)}`}
            >
              {fmtDay(date)} <span className="text-[#525252]">· tap for full day</span>
            </button>
            <div className="space-y-2">
              {rows.map(({ kind, item, when }) => {
                const meta = META(item.session_type);
                if (kind === "offering") {
                  const mine = enrolledOfferingIds.has(item.id);
                  const full = (item.spaces_left ?? 1) <= 0;
                  return (
                    <Link
                      key={`o-${item.id}`}
                      to={!isCoach && mine ? `/session/${item.id}` : (pastPath(item) || `/offerings#offering-${item.id}`)}
                      onClick={(e) => { if (!(!isCoach && mine) && !pastPath(item) && !confirmIfExpired(item.start)) e.preventDefault(); }}
                      data-testid={`agenda-offering-${item.id}`}
                      className="card-surface p-3 sm:p-4 flex items-center gap-3 min-h-[56px] border-l-4 hover:bg-[#141414] active:scale-[0.99] transition-all"
                      style={{ borderLeftColor: mine ? "#CCFF00" : meta.color }}
                    >
                      <div className="text-center w-14 shrink-0">
                        <div className="label-mono text-[#A3A3A3]">{fmtTime(when)}</div>
                        <div className="text-[10px] uppercase tracking-wider text-[#525252]">{item.duration_min}m</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">
                          {mine && <span className="text-[#CCFF00]">★ </span>}{item.title}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mt-0.5 truncate">
                          {meta.label} · {item.venue || "—"} · {(item.enrolled_student_ids?.length || 0)}/{item.max_spaces || "?"} {full ? "(full)" : ""}
                        </div>
                      </div>
                      <span className="label-mono text-[10px] text-[#525252] shrink-0">Offering</span>
                    </Link>
                  );
                }
                const cancelled = isCancelled(item);
                return (
                  <button
                    type="button"
                    key={`b-${item.id}`}
                    onClick={() => (!isCoach ? agendaNav(`/session/${item.id}`)
                      : (!cancelled && pastPath(item, item.offering_id)) ? agendaNav(pastPath(item, item.offering_id)) : onDayClick(date))}
                    data-testid={cancelled ? `agenda-booking-cancelled-${item.id}` : `agenda-booking-${item.id}`}
                    className={`card-surface p-3 sm:p-4 flex items-center gap-3 min-h-[56px] border-l-4 hover:bg-[#141414] active:scale-[0.99] transition-all w-full text-left ${cancelled ? "opacity-50" : ""}`}
                    style={{ borderLeftColor: meta.color, textDecoration: cancelled ? "line-through" : undefined }}
                  >
                    <div className="text-center w-14 shrink-0">
                      <div className="label-mono text-[#A3A3A3]">{fmtTime(when)}</div>
                      <div className="text-[10px] uppercase tracking-wider text-[#525252]">{item.duration_min}m</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">
                        {isCoach ? (item.student_name || "Session") : meta.label}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mt-0.5 truncate">
                        {meta.label} · {item.venue || "—"}{cancelled ? " · CANCELLED" : ""}
                      </div>
                    </div>
                    <span className="label-mono text-[10px] text-[#525252] shrink-0">Booking</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Iter91 — Rolling View. Horizontal 5-week timeline: previous week, current
// week (TODAY cell highlighted in Rally Lab volt-yellow), then next 3 weeks.
// Colour code: volt-yellow border for coach's own sessions, blue border for
// team-shared sessions. Clicking any day opens the existing DayModal so all
// existing coach actions (edit, cancel, rebook) still work.
function RollingView({ bookings, offerings, currentUserId, isCoach, isCancelled, onDayClick, onDayContext, nav }) {
  const pastPath = usePastSessionPath();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon…
  // Monday-start weeks feel right for coaching diaries. Compute this week's Mon.
  const monOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const currentMonday = new Date(today); currentMonday.setDate(today.getDate() + monOffset);
  // Prev week + current + 3 ahead = 5 weeks total, always aligned Mon-Sun.
  const weekStart = new Date(currentMonday); weekStart.setDate(currentMonday.getDate() - 7);

  // Iter91b — week-copy modal state. Only opened via the per-week Copy button.
  const [copyState, setCopyState] = useState(null); // { weekDays: Date[], ownOfferings: [...] }

  const weeks = [];
  for (let w = 0; w < 5; w += 1) {
    const days = [];
    for (let d = 0; d < 7; d += 1) {
      const day = new Date(weekStart); day.setDate(weekStart.getDate() + w * 7 + d);
      days.push(day);
    }
    weeks.push(days);
  }

  const dayKey = (d) => d.toISOString().slice(0, 10);
  // Bucket everything into per-day lists.
  const byDay = new Map();
  const push = (d, entry) => {
    const key = dayKey(d);
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(entry);
  };
  (bookings || []).forEach((b) => {
    const d = new Date(b.start); d.setHours(0,0,0,0);
    push(d, { kind: "booking", item: b, when: new Date(b.start) });
  });
  (offerings || []).forEach((o) => {
    const d = new Date(o.start); d.setHours(0,0,0,0);
    push(d, { kind: "offering", item: o, when: new Date(o.start) });
  });

  const todayKey = dayKey(today);
  const fmtTime = (d) => d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="space-y-2 fade-up d2" data-testid="rolling-view">
      {weeks.map((days, wi) => {
        const label = wi === 0 ? "Last week" : wi === 1 ? "This week" : wi === 2 ? "Next week" : `In ${wi - 1} weeks`;
        // Iter91b — only show Copy on weeks that have at least one of the
        // caller's own offerings, and only for coaches. No point in offering
        // to copy an empty week or someone else's sessions.
        const ownOfferingsThisWeek = isCoach
          ? (offerings || []).filter((o) => {
              const d = new Date(o.start);
              return o.coach_id === currentUserId && d >= days[0] && d <= new Date(days[6].getTime() + 24 * 60 * 60 * 1000 - 1);
            })
          : [];
        return (
          <div key={wi} data-testid={`rolling-week-${wi}`}>
            <div className="flex items-center justify-between mb-1.5 gap-2">
              <div className="label-mono text-[10px] text-[#A3A3A3]">
                {label} · {days[0].toLocaleDateString(undefined,{day:"numeric",month:"short"})} – {days[6].toLocaleDateString(undefined,{day:"numeric",month:"short"})}
              </div>
              {ownOfferingsThisWeek.length > 0 && (
                <button
                  type="button"
                  onClick={() => setCopyState({ weekDays: days, ownOfferings: ownOfferingsThisWeek })}
                  className="min-h-[28px] text-[9px] uppercase tracking-wider px-2 py-1 border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black active:scale-95 transition-all"
                  data-testid={`week-copy-btn-${wi}`}
                >
                  Copy → ({ownOfferingsThisWeek.length})
                </button>
              )}
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {days.map((day) => {
                const key = dayKey(day);
                const isToday = key === todayKey;
                const isPast = day < today;
                const entries = (byDay.get(key) || []).sort((a,b) => a.when - b.when);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => onDayClick(day)}
                    onContextMenu={(e) => onDayContext && onDayContext(day, e)}
                    data-testid={`rolling-day-${key}`}
                    className={`text-left border p-1.5 min-h-[92px] transition-all active:scale-[0.98] ${isToday ? "bg-[#CCFF00] text-black" : "bg-[#0A0A0A] hover:bg-[#141414]"}`}
                    style={{ borderColor: isToday ? "#CCFF00" : "#262626", opacity: isPast && !isToday ? 0.55 : 1 }}
                  >
                    <div className={`flex items-center justify-between text-[10px] uppercase tracking-wider ${isToday ? "text-black font-bold" : "text-[#525252]"}`}>
                      <span>{day.toLocaleDateString(undefined, { weekday: "short" })}</span>
                      <span>{day.getDate()}</span>
                    </div>
                    {isToday && <div className="label-mono text-[9px] text-black font-bold mt-0.5" data-testid="rolling-today-marker">TODAY</div>}
                    <div className="mt-1 space-y-1">
                      {entries.slice(0, 3).map((e, i) => {
                        const own = e.item.coach_id === currentUserId;
                        const stripe = isCoach ? (own ? "#CCFF00" : "#5B8DEF") : (META(e.item.session_type).color);
                        const cancelled = e.kind === "booking" && isCancelled(e.item);
                        // Iter92 — offerings navigate to that specific
                        // offering's detail card (via /offerings#offering-{id}).
                        // Bookings continue to open the DayModal via the
                        // parent-cell click so coaches can act on them there.
                        const goToOffering = (ev) => {
                          if (!isCoach && e.kind === "booking") { ev.stopPropagation(); nav(`/session/${e.item.id}`); return; }
                          if (e.kind === "booking" && !cancelled && pastPath(e.item, e.item.offering_id)) { ev.stopPropagation(); nav(pastPath(e.item, e.item.offering_id)); return; }
                          if (e.kind !== "offering") return;
                          ev.stopPropagation();
                          if (!isCoach && (e.item.enrolled_student_ids || []).includes(currentUserId)) { nav(`/session/${e.item.id}`); return; }
                          if (pastPath(e.item)) { nav(pastPath(e.item)); return; }
                          if (!confirmIfExpired(e.item.start)) return;
                          nav(`/offerings#offering-${e.item.id}`);
                        };
                        return (
                          <div
                            key={i}
                            onClick={goToOffering}
                            className={`text-[9px] pl-1.5 border-l-2 truncate ${e.kind === "offering" || !isCoach ? "cursor-pointer hover:brightness-110" : ""}`}
                            style={{
                              borderColor: stripe,
                              color: isToday ? "#0A0A0A" : "#E5E5E5",
                              textDecoration: cancelled ? "line-through" : undefined,
                              opacity: cancelled ? 0.5 : 1,
                            }}
                            data-testid={`rolling-entry-${e.kind}-${e.item.id}`}
                          >
                            {fmtTime(e.when)}{" "}
                            <span className={isToday ? "text-[#525252]" : "text-[#A3A3A3]"}>
                              {isCoach ? (e.item.student_name || e.item.title || META(e.item.session_type).label) : META(e.item.session_type).label}
                            </span>
                          </div>
                        );
                      })}
                      {entries.length > 3 && (
                        <div className={`text-[9px] ${isToday ? "text-[#525252]" : "text-[#525252]"}`}>
                          + {entries.length - 3} more
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      <WeekCopyModal state={copyState} onClose={() => setCopyState(null)} existingOfferings={offerings} />
    </div>
  );
}

// Iter91b — Copy modal. Presents source-week's offerings as a Select-All /
// per-day / per-session checkbox tree, lets the coach pick a target week
// (+1..+4 ahead), previews the count, then POSTs to /offerings/{id}/duplicate
// in parallel. Skips slots where the target already has a same-day+time
// offering so we don't accidentally double-book the calendar.
function WeekCopyModal({ state, onClose, existingOfferings }) {
  const [selected, setSelected] = useState(() => new Set());
  const [weekOffset, setWeekOffset] = useState(1);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!state) return;
    setSelected(new Set(state.ownOfferings.map((o) => o.id)));
    setWeekOffset(1);
  }, [state]);
  if (!state) return null;
  const { weekDays, ownOfferings } = state;

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = () => setSelected((prev) => (
    prev.size === ownOfferings.length ? new Set() : new Set(ownOfferings.map((o) => o.id))
  ));
  const toggleDay = (_dayKey, dayOfferings) => setSelected((prev) => {
    const n = new Set(prev);
    const allSelected = dayOfferings.every((o) => n.has(o.id));
    dayOfferings.forEach((o) => { if (allSelected) n.delete(o.id); else n.add(o.id); });
    return n;
  });

  const byDay = new Map();
  ownOfferings.forEach((o) => {
    const key = new Date(o.start).toDateString();
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(o);
  });
  byDay.forEach((arr) => arr.sort((a, b) => new Date(a.start) - new Date(b.start)));

  const targetOffsetMs = weekOffset * 7 * 24 * 60 * 60 * 1000;
  const targetStart = new Date(weekDays[0].getTime() + targetOffsetMs);
  const targetEnd = new Date(weekDays[6].getTime() + targetOffsetMs);
  const fmtDate = (d) => d.toLocaleDateString(undefined, { day: "numeric", month: "short" });

  const targetOfferings = (existingOfferings || []).filter((o) => {
    const d = new Date(o.start);
    return d >= targetStart && d <= new Date(targetEnd.getTime() + 24*60*60*1000 - 1);
  });
  const isConflict = (src) => {
    const t = new Date(new Date(src.start).getTime() + targetOffsetMs);
    return targetOfferings.some((o) => Math.abs(new Date(o.start) - t) < 60_000);
  };

  const doable = ownOfferings.filter((o) => selected.has(o.id) && !isConflict(o));
  const conflicts = ownOfferings.filter((o) => selected.has(o.id) && isConflict(o));

  const confirm = async () => {
    if (doable.length === 0) return;
    setBusy(true);
    try {
      await Promise.all(doable.map((o) => {
        const t = new Date(new Date(o.start).getTime() + targetOffsetMs);
        return api.post(`/offerings/${o.id}/duplicate`, { start: t.toISOString() });
      }));
      toast.success(`Copied ${doable.length} session${doable.length === 1 ? "" : "s"} to ${fmtDate(targetStart)} – ${fmtDate(targetEnd)}`);
      onClose();
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Copy failed — some sessions may have been created before the error");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      data-testid="copy-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg card-surface p-5 border border-[#CCFF00] max-h-[85vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <div className="label-mono text-[#CCFF00]">COPY SESSIONS</div>
            <h3 className="h-display text-xl tracking-tight mt-1">
              From: {fmtDate(weekDays[0])} – {fmtDate(weekDays[6])}
            </h3>
          </div>
          <button
            type="button" onClick={onClose}
            className="min-h-[36px] px-3 text-xs uppercase tracking-wider border border-[#262626] hover:border-white active:scale-95 transition-all"
            data-testid="copy-modal-close"
          >
            Close
          </button>
        </div>

        <label className="block text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1">To</label>
        <select
          value={weekOffset}
          onChange={(e) => setWeekOffset(Number(e.target.value))}
          data-testid="copy-target-selector"
          className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm mb-4 focus:border-[#CCFF00] outline-none"
        >
          {[1,2,3,4].map((n) => {
            const s = new Date(weekDays[0].getTime() + n * 7 * 24 * 60 * 60 * 1000);
            const e2 = new Date(weekDays[6].getTime() + n * 7 * 24 * 60 * 60 * 1000);
            return <option key={n} value={n}>+{n} week{n===1?"":"s"} ({fmtDate(s)} – {fmtDate(e2)})</option>;
          })}
        </select>

        <label className="flex items-center gap-2 text-xs uppercase tracking-wider text-white cursor-pointer border-b border-[#262626] pb-2 mb-2">
          <input
            type="checkbox"
            className="w-4 h-4 accent-[#CCFF00]"
            data-testid="copy-select-all"
            checked={selected.size === ownOfferings.length}
            onChange={toggleAll}
          />
          Select all ({ownOfferings.length} session{ownOfferings.length === 1 ? "" : "s"})
        </label>

        <div className="space-y-3">
          {Array.from(byDay.entries()).map(([dayKeyStr, dayOfferings]) => {
            const dayDate = new Date(dayKeyStr);
            const dayId = dayDate.toISOString().slice(0,10);
            const allDaySelected = dayOfferings.every((o) => selected.has(o.id));
            return (
              <div key={dayKeyStr}>
                <label className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#CCFF00] cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-[#CCFF00]"
                    data-testid={`copy-day-${dayId}`}
                    checked={allDaySelected}
                    onChange={() => toggleDay(dayKeyStr, dayOfferings)}
                  />
                  {dayDate.toLocaleDateString(undefined,{weekday:"long"})} · {dayOfferings.length} session{dayOfferings.length === 1 ? "" : "s"}
                </label>
                <div className="ml-6 mt-1 space-y-1">
                  {dayOfferings.map((o) => {
                    const conflict = isConflict(o);
                    return (
                      <label key={o.id} className="flex items-start gap-2 text-xs text-[#E5E5E5] cursor-pointer" title={conflict ? "Target slot already has an offering at this day+time — will be skipped" : ""}>
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-[#CCFF00] mt-0.5"
                          data-testid={`copy-session-${o.id}`}
                          checked={selected.has(o.id)}
                          onChange={() => toggle(o.id)}
                        />
                        <span className={`flex-1 ${conflict ? "opacity-40 line-through" : ""}`}>
                          {new Date(o.start).toLocaleTimeString(undefined,{hour:"2-digit",minute:"2-digit"})} · {o.title || META(o.session_type).label} · {o.venue || "—"}
                          {conflict && <span className="ml-2 text-[9px] uppercase tracking-wider text-[#FFB020]">CLASH — SKIP</span>}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 pt-3 border-t border-[#262626] text-xs text-[#A3A3A3]" data-testid="copy-preview">
          {doable.length} session{doable.length === 1 ? "" : "s"} will land on {fmtDate(targetStart)} – {fmtDate(targetEnd)}.
          {conflicts.length > 0 && (
            <span className="text-[#FFB020]"> ({conflicts.length} clash{conflicts.length === 1 ? "" : "es"} skipped.)</span>
          )}
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button" onClick={onClose} disabled={busy}
            className="min-h-[40px] px-4 text-xs uppercase tracking-wider border border-[#262626] hover:border-white active:scale-95 transition-all disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button" onClick={confirm} disabled={busy || doable.length === 0}
            className="btn-volt min-h-[40px] px-4 text-xs uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-40 active:scale-95 transition-all"
            data-testid="copy-confirm"
          >
            {busy ? "Copying…" : `Copy ${doable.length} session${doable.length === 1 ? "" : "s"} →`}
          </button>
        </div>
      </div>
    </div>
  );
}




